import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

interface BlogPost {
  id: string;
  title: string | null;
  content: string | null;
  slug: string | null;
  keyword: string | null;
  meta_description: string | null;
  published_at: string | null;
  created_at: string;
  featured_image: string | null;
  is_pillar: boolean;
  indexed_at: string | null;
}

type PostStatus = "published" | "processing" | "queued";

const getStatus = (post: BlogPost): PostStatus => {
  if (post.published_at) return "published";
  if (post.content) return "processing";
  return "queued";
};

const statusConfig: Record<PostStatus, { label: string; classes: string }> = {
  published: { label: "Published", classes: "bg-primary/10 text-primary" },
  processing: { label: "Processing", classes: "bg-secondary text-secondary-foreground" },
  queued: { label: "Queued", classes: "bg-muted text-muted-foreground" },
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

interface Props {
  clientIds: string[];
}

const ContentTab = ({ clientIds }: Props) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!clientIds.length) {
      setLoading(false);
      return;
    }
    const fetch = async () => {
      const { data } = await supabase
        .from("blog_posts")
        .select("*")
        .in("client_id", clientIds)
        .order("created_at", { ascending: false });
      if (data) setPosts(data as BlogPost[]);
      setLoading(false);
    };
    fetch();
    const interval = setInterval(fetch, 10000);
    return () => clearInterval(interval);
  }, [clientIds]);

  const sortedPosts = useMemo(() => {
    return [...posts].sort((a, b) => {
      if (a.is_pillar && !b.is_pillar) return -1;
      if (!a.is_pillar && b.is_pillar) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [posts]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-2xl border border-border bg-muted" />
        ))}
      </div>
    );
  }

  return (
    <div className="text-foreground">
      <h1 className="mb-1 text-2xl font-bold" style={{ fontFamily: "'DM Sans', 'Plus Jakarta Sans', sans-serif" }}>
        Content
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">{posts.length} pages created</p>


      {posts.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-8 text-center shadow-[0_2px_12px_hsl(var(--foreground)/0.06)]">
          <p className="text-sm text-muted-foreground">Content is being prepared. Check back soon.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedPosts.map((post, i) => {
            const status = getStatus(post);
            const cfg = statusConfig[status];
            const isExpanded = expandedId === post.id;

            return (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <button
                  onClick={() => setExpandedId(isExpanded ? null : post.id)}
                  className="w-full rounded-2xl border border-border bg-card p-4 text-left shadow-[0_2px_12px_hsl(var(--foreground)/0.04)] transition-all hover:shadow-md"
                >
                  {post.featured_image && (
                    <img src={post.featured_image} alt={post.keyword || "Article"} className="mb-3 w-full rounded-xl" />
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">
                        {post.title || "Untitled Page"}
                      </p>
                      {post.keyword && (
                        <p className="mt-0.5 truncate text-xs text-primary">
                          {typeof post.keyword === "string" && post.keyword.startsWith("{")
                            ? (() => { try { return JSON.parse(post.keyword).keyword || post.keyword; } catch { return post.keyword; } })()
                            : post.keyword}
                        </p>
                      )}
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {post.published_at ? `Published ${timeAgo(post.published_at)}` : timeAgo(post.created_at)}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.classes}`}>
                        {post.is_pillar ? "PILLAR" : cfg.label}
                      </span>
                      {post.indexed_at && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                          Indexed ✓
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                <AnimatePresence>
                  {isExpanded && post.content && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="rounded-b-2xl border-x border-b border-border bg-card p-5">
                        <div className="article-content prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: post.content }} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContentTab;
